import * as React from 'react';

import { DetailsList, DetailsListLayoutMode, IColumn, Selection, SelectionMode, CheckboxVisibility } from '@fluentui/react/lib/DetailsList';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { MarqueeSelection } from '@fluentui/react/lib/MarqueeSelection';
import { Dropdown } from '@fluentui/react/lib/Dropdown';
import { entityCriteriaScreen, toggleLoading, ExistingCheck, summaryScreen, errorScreen } from '../actions/navigationWorker';
import { getEntityTypeText, APICalls, getXplanURL, getPayload, handleResponseData } from '../actions/helper';
import { has_capability } from '../actions/entityWorker';
import NoteProperties from './NoteProperties';
import ActiveTaskProperties from './ActiveTaskProperties';
import FiledNoteProperties from './FiledNoteProperties';
import NewTaskProperties from './NewTaskProperties';
import axios from 'axios';
import { LogError } from '../actions/errorWorker';

export interface AppProps {
    hide: boolean;
    data: Array<JSON>;
}

export interface AppState {
    columns: IColumn[],
    selected: any,
    data: any,
    selectedFileTo: string,
    activeTaskData: any[],
    filedNoteData: any[]
}

class SearchResults extends React.Component<AppProps, AppState> {
    private _selection: Selection;
    private originalScrollIntoView: any;

    constructor(props) {
        super(props);

        this._selection = new Selection({
          onSelectionChanged: () => {
            this.setState({
              selected: this._getSelectionDetails()
            });
          }
        });
        
        // Disable auto-scroll on hover in dropdowns by overriding scrollIntoView
        this.disableAutoScrollInDropdowns();
        
        // Prevent focus changes on hover
        this.preventDropdownFocusOnHover();

        const file_type = JSON.parse(sessionStorage.getItem("file_to_type")) || { id: "note" };

        this.state = {
            columns: this._getColumns(),
            selected: null,
            data: this.makeRows(),
            selectedFileTo: file_type.id,
            activeTaskData: [],
            filedNoteData: []
        };
    }

    disableAutoScrollInDropdowns = () => {
        // Save original scrollIntoView
        this.originalScrollIntoView = Element.prototype.scrollIntoView;
        
        // Override scrollIntoView to prevent auto-scrolling on hover
        Element.prototype.scrollIntoView = function(arg?: any) {
            // Check if element is inside a dropdown or callout
            if (this.closest) {
                const inDropdown = this.closest('.ms-Dropdown-items') || 
                                 this.closest('.ms-Dropdown-itemsWrapper') ||
                                 this.closest('.ms-Callout-main') ||
                                 this.closest('.ms-Dropdown-callout') ||
                                 this.classList?.contains('ms-Dropdown-item');
                
                if (inDropdown) {
                    // Block ALL scrollIntoView calls inside dropdowns
                    return;
                }
            }
            // Allow scrollIntoView for other cases
            return this.originalScrollIntoView.call(this, arg);
        };
    }

    restoreScrollIntoView = () => {
        if (this.originalScrollIntoView) {
            Element.prototype.scrollIntoView = this.originalScrollIntoView;
        }
    }

    preventDropdownFocusOnHover = () => {
        // Add event listener to prevent focus on hover in dropdowns
        document.addEventListener('mousemove', (e) => {
            const target = e.target as HTMLElement;
            if (target && typeof target.closest === 'function') {
                const dropdownItem = target.closest('.ms-Dropdown-item');
                if (dropdownItem) {
                    // Prevent focus by stopping the event
                    e.stopPropagation();
                    // Remove focus from the item if it has it
                    const focusedElement = document.activeElement as HTMLElement;
                    if (focusedElement && focusedElement.closest('.ms-Dropdown-item')) {
                        focusedElement.blur();
                    }
                }
            }
        }, true);
    }

    Back_to_Criteria = () => {
        this.setState({
            data: null
          });
        entityCriteriaScreen(true);
    }

    handleFileToTypeChange = async (_event, item) => {
        this.setState({ selectedFileTo: item.key });
        sessionStorage.setItem("file_to_type", JSON.stringify({ id: item.key, name: item.text }));
        
        // Load active tasks if that option is selected
        if (item.key === "activetask") {
            await this.loadActiveTasks();
        } else if (item.key === "existingnote") {
            await this.loadFiledNotes();
        }
    }

    loadFiledNotes = async () => {
        const selected_clients = JSON.parse(sessionStorage.getItem("selected_clients")) || [];
        
        if (selected_clients?.length > 1) {
            errorScreen(
                false,
                "",
                "",
                "Filing to multiple entities on filed notes is not permitted.",
                {
                    hideRetry: true,
                    hideTitle: true,
                    backTitle: "Back",
                }
            );
            return;
        }

        toggleLoading(true);
        
        try {
            const data = [];
            for (let i = 0; i < selected_clients.length; i++) {
                const entity_ids = "&entity_ids.0=" + selected_clients[i]["id"];
                const payload = getPayload(
                    getXplanURL() + APICalls.FiledNotes.replace("{2}", entity_ids),
                    null,
                    "GET"
                );

                const response = await axios.post(APICalls.AWSUrl(), payload);
                const result_data = handleResponseData(
                    response,
                    "No Filed Notes found for this client",
                    true
                );

                const isError = result_data && result_data.isError;
                if (isError) {
                    throw result_data.message;
                }

                if (result_data) data.push(...result_data);
            }

            if (data.length) {
                this.processFiledNoteData(data);
            } else {
                this.setState({ filedNoteData: [] });
                toggleLoading(false);
            }
        } catch (error) {
            errorScreen(false, "", "", error.toString());
            LogError(error, {}, "loadFiledNotes", "ERROR");
            toggleLoading(false);
        }
    }

    processFiledNoteData = (data) => {
        const items = [];
        try {
            for (let item_count = 0; item_count < data.length; item_count++) {
                const note_date_raw =
                    data[item_count]["reference_date"]?._val ||
                    data[item_count]["note_date"]?._val ||
                    data[item_count]["created_date"]?._val ||
                    data[item_count]["date"]?._val ||
                    data[item_count]["reference_date"] ||
                    data[item_count]["note_date"] ||
                    data[item_count]["created_date"] ||
                    data[item_count]["date"];
                const note_date = note_date_raw ? String(note_date_raw).substring(0, 10) : "";
                
                const note_subject = data[item_count]["subject"] || "";
                const note_type = data[item_count]["doctype"] || "";
                const note_subtype = data[item_count]["docsubtype"] || "";
                const note_id = data[item_count]["id"] || data[item_count]["docid"] || data[item_count]["doc_id"];
                if (!note_id) {
                    continue;
                }

                const details = `Subject: ${note_subject}|break|Type: ${note_type}|break|Subtype: ${note_subtype}|break|Date: ${note_date}`;

                const fieldRow = {
                    id: note_id,
                    subject: note_subject,
                    details: details
                };
                items.push(fieldRow);
            }

            this.setState({ filedNoteData: items });
            toggleLoading(false);
        } catch (error) {
            errorScreen(false, "", "", error.toString());
            LogError(error, {}, "processFiledNoteData", "ERROR");
            toggleLoading(false);
        }
    }

    loadActiveTasks = async () => {
        const selected_clients = JSON.parse(sessionStorage.getItem("selected_clients")) || [];
        
        if (selected_clients?.length > 1) {
            errorScreen(
                false,
                "",
                "",
                "Filing to multiple entities on active tasks is not permitted.",
                {
                    hideRetry: true,
                    hideTitle: true,
                    backTitle: "Back",
                }
            );
            return;
        }

        toggleLoading(true);
        
        try {
            const data = [];
            for (let i = 0; i < selected_clients.length; i++) {
                const entity_ids = "&entity_ids.0=" + selected_clients[i]["id"];
                const payload = getPayload(
                    getXplanURL() + APICalls.ActiveTasks.replace("{2}", entity_ids),
                    null,
                    "GET"
                );

                const response = await axios.post(APICalls.AWSUrl(), payload);
                const result_data = handleResponseData(
                    response,
                    "No Active Task found for this client",
                    true
                );

                const isError = result_data && result_data.isError;
                if (isError) {
                    throw result_data.message;
                }

                if (result_data) data.push(...result_data);
            }

            if (data.length) {
                this.processActiveTaskData(data);
            } else {
                this.setState({ activeTaskData: [] });
                toggleLoading(false);
            }
        } catch (error) {
            errorScreen(false, "", "", error.toString());
            LogError(error, {}, "loadActiveTasks", "ERROR");
            toggleLoading(false);
        }
    }

    processActiveTaskData = (data) => {
        let task_assignee_params = "";
        const task_assignee_list = [];
        let counter = 0;

        for (let item_count = 0; item_count < data.length; item_count++) {
            const task_assignee_id = data[item_count]["assignee"];
            if (task_assignee_list.indexOf(task_assignee_id) < 0) {
                task_assignee_params = task_assignee_params + "&ids." + counter + "=" + task_assignee_id;
                task_assignee_list.push(task_assignee_id);
                counter = counter + 1;
            }
        }
        this.getAdviserDetails(task_assignee_params, data);
    }

    getAdviserDetails = async (entity_ids, task_data) => {
        const items = [];
        const taskkind_lookup = JSON.parse(localStorage.getItem("task_taskkind_results")) || [];
        const subtype_lookup = JSON.parse(localStorage.getItem("task_subtype_results")) || [];

        try {
            const payload = getPayload(getXplanURL() + APICalls.AdviserSearch + entity_ids, null, "GET");
            const response = await axios.post(APICalls.AWSUrl(), payload);
            const adviser_data = handleResponseData(response, "");
            const clients = JSON.parse(sessionStorage.getItem("selected_clients"));
            
            for (let item_count = 0; item_count < task_data.length; item_count++) {
                let task_due_date = task_data[item_count]["duedate"];
                task_due_date = task_due_date ? task_due_date._val : "";

                const task_type_id = task_data[item_count]["taskkind"];
                const task_subtype_id = task_data[item_count]["subtype"];
                const task_assignee_id = task_data[item_count]["assignee"];

                let task_type = taskkind_lookup.find((element) => element[0] === task_type_id);
                if (task_type) task_type = task_type[1];
                
                let task_subtype = subtype_lookup.find((element) => element[0] === task_subtype_id);
                if (task_subtype) task_subtype = task_subtype[1];
                
                let task_assignee;
                if (adviser_data) {
                    task_assignee = adviser_data.find((element) => element.id === task_assignee_id);
                    if (task_assignee) task_assignee = task_assignee.entity_name;
                }

                const task_subject = task_data[item_count]["subject"];
                const task_id = task_data[item_count]["id"];
                const entity = clients.find((x) => x.id === task_data[item_count]["client"]);
                const entityName = entity.name || "";
                
                const fieldRow = {
                    id: task_id,
                    subject: task_subject,
                    type: task_type,
                    subtype: task_subtype,
                    due_date: task_due_date,
                    assignee: task_assignee,
                    name: entityName,
                    details:
                        entityName +
                        "|break|Task ID: " +
                        task_id +
                        "|break|Subject: " +
                        task_subject +
                        "|break|Type: " +
                        task_type +
                        " > " +
                        task_subtype +
                        "|break|Due Date: " +
                        task_due_date +
                        "|break|Assignee: " +
                        task_assignee,
                };
                items.push(fieldRow);
            }

            this.setState({ activeTaskData: items });
            toggleLoading(false);
        } catch (error) {
            errorScreen(false, "", "", error.toString());
            LogError(error, {}, "getAdviserDetails", "ERROR");
            toggleLoading(false);
        }
    }

    handleExistingNotes = () => {
        ExistingCheck();
    }

    handleFileClick = () => {
        // Show summary or file directly based on settings
        summaryScreen();
    }

    private makeRows() {
        if (this.props.data.length === 0) {
            return null;
        }
        let items = [];
        let entity_type = sessionStorage.getItem('selected_entity_type') || '';
        if (entity_type === 'client') {
            for (let item_count = 0; item_count < this.props.data.length; item_count++) {

                let entity_string = this.props.data[item_count]['entity_name'];
                let adviser_name = this.props.data[item_count]['fields']['client_adviser_name'] || '';
                let adviser_string = adviser_name ? '|break|Adviser: ' + adviser_name : '';
                let dob = this.props.data[item_count]['fields'].dob ? this.props.data[item_count]['fields'].dob._val : '';
                let dob_string = dob ? '|break|Date of Birth: ' + dob.toString() : '';
                let postcode = this.props.data[item_count]['fields'].preferred_postcode;
                let postcode_string = postcode ? '|break|Post Code: ' + postcode.toString() : '';
                let new_string = entity_string + adviser_string + dob_string + postcode_string;

                let fieldRow = {
                    id: this.props.data[item_count]['id'],
                    entity_name : entity_string,
                    client_detail : new_string
                   
                };
                items.push(fieldRow);
            }
            
        }
        else{
            for (let item_count = 0; item_count < this.props.data.length; item_count++) {
                let fieldRow = {
                    id: this.props.data[item_count]['id'],
                    entity_name: this.props.data[item_count]['entity_name'],
                    client_detail: this.props.data[item_count]['entity_name'],
                };
                items.push(fieldRow);
            }
        }
        return items;
    }

    private _getColumns(): IColumn[] {
       
        let columns = [];
        let fields = [{
            'field': 'client_detail',
            'header': 'Client Details',
            'min': 80,
            'max': 200
        }];
   

        for (let i = 0; i < fields.length; i++) {
            let field = fields[i];
            let item_column = {
                key: 'col_' + field['field'],
                name: field['header'],
                fieldName: field['field'],
                minWidth: field['min'],
                maxWidth: field['max'],
                isResizable: true
            };
            columns.push(item_column);
        }
        return columns;
    }

    private _getSelectionDetails(): any {
        const selected_items = this._selection.getSelection();
        const selected_entities = [];

        if (selected_items !== null) {
            for (let i = 0; i < selected_items.length; i++) {
                let entity_id = (selected_items[i] as any).id;
                let entity_name = (selected_items[i] as any).entity_name;
                let selected_entity = {id: entity_id, name: entity_name};
                selected_entities.push(selected_entity);
            }
        }
        return selected_entities.length > 0 ? selected_entities : null;
    }

    componentWillUnmount() {
        // Restore original scrollIntoView behavior
        this.restoreScrollIntoView();
    }

    componentDidUpdate(_prevProps: AppProps, prevState: AppState) {
        // If clients are selected and filing type changes, load active tasks if needed
        if (this.state.selected && prevState.selectedFileTo !== this.state.selectedFileTo) {
            if (this.state.selectedFileTo === "activetask") {
                this.loadActiveTasks();
            }
        }
    }

    private _renderItemColumn(item: any, _index: number, column: IColumn) {
        const fieldContent = item[column.fieldName || ''];
        const break_string = '|break|';
        if (column.key === 'col_client_detail') {
            const split_string = fieldContent.split(break_string);
            return split_string.map(t => <span>{t}<br/></span>);
        } else {
            return <span>{fieldContent}</span>;
        }
    }

    render() {
        const file_type = JSON.parse(sessionStorage.getItem("file_to_type")) || { id: "note" };
        const file_to_options = [];

        if (has_capability("edit_entity_note") && has_capability("file_email_as_docnote")) {
            file_to_options.push({ key: "note", text: "Note" });
            file_to_options.push({ key: "existingnote", text: "Existing Notes" });
        }
        if (has_capability("edit_tasks") && has_capability("file_email_as_task")) {
            file_to_options.push({ key: "newtask", text: "New Task" });
            file_to_options.push({ key: "activetask", text: "Active Task" });
        }

        const responsiveDropdownProps = {
            calloutProps: {
                calloutMaxHeight: window.innerHeight * 0.6,
                calloutMinWidth: 300
            },
            dropdownWidth: "auto" as "auto",
            responsiveMode: 5,
            styles: {
                dropdown: { minHeight: '44px' },
                title: { minHeight: '44px', lineHeight: '42px' },
                callout: { 
                    maxHeight: '60vh',
                    minWidth: '300px',
                    width: 'auto'
                },
                dropdownItemsWrapper: {
                    minWidth: '300px'
                },
                dropdownItems: {
                    minWidth: '300px'
                },
                dropdownItem: {
                    minWidth: '300px',
                    whiteSpace: 'normal',
                    height: 'auto',
                    lineHeight: '20px',
                    padding: '8px'
                },
                dropdownItemSelected: {
                    minWidth: '300px'
                }
            },
        };

        return (
            <div style={{ fontSize: '13px', padding: '16px', backgroundColor: '#f5f5f7' }}>
                <div style={{ 
                    padding: '16px', 
                    marginBottom: '16px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    borderTop: '3px solid #5D3FD6'
                }}>
                    <Dropdown
                        label="Please select a filing option"
                        options={file_to_options}
                        selectedKey={file_type.id}
                        onChange={this.handleFileToTypeChange}
                        defaultSelectedKey={this.state.selectedFileTo}
                        styles={{ root: { width: '100%' }, ...responsiveDropdownProps.styles }}
                        calloutProps={responsiveDropdownProps.calloutProps}
                        responsiveMode={responsiveDropdownProps.responsiveMode}
                        dropdownWidth={responsiveDropdownProps.dropdownWidth}
                    />
                </div>

                <div style={{
                    padding: '16px',
                    marginBottom: '16px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    borderTop: '3px solid #23F0C7'
                }}>
                    <p style={{ marginBottom: '12px', fontSize: '13px', color: '#1A1F4D', fontWeight: 600 }}>Please select the {getEntityTypeText(sessionStorage.getItem('selected_entity_type'))}(s) that you would like to file the current email to:</p>
                    <div style={{overflow: 'auto', maxHeight: '40vh', WebkitOverflowScrolling: 'touch'}} data-is-scrollable='false'>
                    <MarqueeSelection selection={this._selection}>
                        <DetailsList
                            items={this.state.data}
                            columns={this._getColumns()}
                            setKey='set'
                            selectionMode={SelectionMode.multiple}
                            layoutMode={DetailsListLayoutMode.fixedColumns}
                            selectionPreservedOnEmptyClick={true}
                            enterModalSelectionOnTouch={true}
                            checkboxVisibility={CheckboxVisibility.always}
                            isHeaderVisible={true}
                            compact={true}
                            selection={this._selection}
                            onRenderItemColumn={this._renderItemColumn}
                        />
                    </MarqueeSelection>
                    </div>
                </div>

                {this.state.selected && (
                    <div style={{ 
                        marginTop: '0px',
                        padding: '16px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        borderTop: '3px solid #5D3FD6'
                    }}>
                        {this.state.selectedFileTo === "note" && <NoteProperties hide={false} />}
                        {this.state.selectedFileTo === "activetask" && <ActiveTaskProperties hide={false} data={this.state.activeTaskData} />}
                        {this.state.selectedFileTo === "existingnote" && <FiledNoteProperties hide={false} data={this.state.filedNoteData} />}
                        {this.state.selectedFileTo === "newtask" && <NewTaskProperties hide={false} data={[]} />}
                        
                        <div style={{ display: 'flex', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
                            <DefaultButton 
                                data-automation-id='btnExistingNotes' 
                                text='Filed Notes' 
                                onClick={this.handleExistingNotes}
                                styles={{ 
                                    root: {  
                                        flex: '1', 
                                        minWidth: '140px', 
                                        minHeight: '44px', 
                                        fontSize: '13px',
                                        borderRadius: '6px',
                                        border: '2px solid #5D3FD6',
                                        color: '#5D3FD6',
                                        fontWeight: 500,
                                        backgroundColor: '#ffffff'
                                    },
                                    rootHovered: {
                                        backgroundColor: '#f8f7fc',
                                        border: '2px solid #4a32ab',
                                        color: '#4a32ab'
                                    }
                                }}
                            />
                            <PrimaryButton 
                                data-automation-id='btnFile' 
                                text='File' 
                                onClick={this.handleFileClick}
                                styles={{ 
                                    root: { 
                                        flex: '1', 
                                        minWidth: '140px', 
                                        minHeight: '44px', 
                                        fontSize: '13px',
                                        borderRadius: '6px',
                                        backgroundColor: '#5D3FD6',
                                        border: 'none',
                                        fontWeight: 500,
                                        color: '#ffffff'
                                    },
                                    rootHovered: {
                                        backgroundColor: '#4a32ab'
                                    },
                                    rootPressed: {
                                        backgroundColor: '#3b2888'
                                    }
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default SearchResults;
